import { Button, Text } from "@mantine/core";
import * as Sentry from "@sentry/react";
import IconLink from "@src/assets/icons/tabler/link.svg?react";
import type { WhiteboardDocumentId } from "@src/components/collaboration/document-id";
import { AppQrCode } from "@src/components/qr-code/app-qr-code";
import {
	downloadSvgById,
	openExternalLink,
} from "@src/components/qr-code/qr-code-utils";
import type { SharePanelLinkContext } from "@src/utils/analytics";
import { captureQrCodeDownloaded } from "@src/utils/analytics";
import { showErrorNotification } from "@src/utils/notifications";
import clsx from "clsx";
import { useCallback, useMemo } from "react";

type SharePanelQrSectionProps = {
	boardId: WhiteboardDocumentId;
	qrCodeId: string;
	link: string;
	title: string;
	description: string;
	filename: string;
	downloadAriaLabel: string;
	openAriaLabel?: string;
	withOpenButton?: boolean;
	analyticsContext?: SharePanelLinkContext;
};

export function SharePanelQrSection({
	boardId,
	qrCodeId,
	link,
	title,
	description,
	filename,
	downloadAriaLabel,
	openAriaLabel,
	withOpenButton = true,
	analyticsContext,
}: SharePanelQrSectionProps) {
	const openLinkIcon = useMemo(
		() => <IconLink className="w-3.5 h-3.5" aria-hidden />,
		[],
	);

	const handleDownloadQrCode = useCallback(() => {
		try {
			downloadSvgById(qrCodeId, filename);
			if (analyticsContext) {
				captureQrCodeDownloaded(boardId, analyticsContext);
			}
		} catch (error) {
			Sentry.captureException(error);
			showErrorNotification("Error", "Failed to download QR code");
		}
	}, [boardId, filename, qrCodeId, analyticsContext]);

	const handleOpenLink = useCallback(() => {
		openExternalLink(link);
	}, [link]);

	return (
		<div className="border-t border-border-subtle pt-4">
			<div
				className={clsx(
					"rounded-2xl border border-brand-primary/10 p-4",
					"bg-linear-to-br from-brand-primary/4 via-surface-subtle",
					"to-brand-primary/8",
				)}
			>
				<div className="flex flex-col gap-4">
					<div className="space-y-1">
						<Text size="sm" fw={700} className="text-text-primary">
							{title}
						</Text>
						<Text size="xs" className="text-text-secondary" lh={1.5}>
							{description}
						</Text>
					</div>
					<div
						className={clsx(
							"mx-auto w-full max-w-33 rounded-3xl",
							"border border-border-subtle bg-white p-3 shadow-sm",
							"sm:max-w-48 sm:p-3.5",
						)}
					>
						<AppQrCode
							id={qrCodeId}
							value={link}
							size={160}
							className="block h-auto w-full text-text-primary"
						/>
					</div>
					<div className="flex flex-row gap-2">
						<Button
							size="md"
							variant="light"
							color="var(--ui-color-brand-primary)"
							onClick={handleDownloadQrCode}
							className="flex-1"
							aria-label={downloadAriaLabel}
						>
							Download QR
						</Button>
						{withOpenButton && (
							<Button
								size="md"
								variant="light"
								color="var(--ui-color-brand-primary)"
								onClick={handleOpenLink}
								leftSection={openLinkIcon}
								className="flex-1"
								aria-label={openAriaLabel}
							>
								Open
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
